import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrayValidationMode, TmState, ValidationRule, VALIDATION_OPTIONS } from "./TestCaseTypes";
import { InfoIcon, CheckMiniIcon, ChevronDownSmIcon, MinusMiniIcon } from "./svgs/TestCaseSvgs";
import CustomToolTip from "../../../CommonComponents/CustomToolTip";
import { formatTreeHeaderLabel, createDefaultRule } from "./TestCaseHelpers";

interface TmParamPanelProps {
  params: { key: string; displayName: string }[];
  state: TmState | undefined;
  onToggle: (key: string) => void;
  onUpdateRule: (key: string, updates: Partial<ValidationRule>) => void;
  onRegisterArrayGroup: (group: { basePath: string; itemType: "primitive" | "object"; objectFields: string[]; indices: number[] }) => void;
  onSetArrayMode?: (basePath: string, mode: ArrayValidationMode) => void;
  showScroll?: boolean;
  enumMappings?: Record<string, Record<string, string>>;
  showInfo?: boolean;
  tooltipPosition?: "left" | "right";
}

type ParamItem = { key: string; displayName: string };

// ─── Recursive display-tree model ──────────────────────────────
// Replaces the old flat "SingleParam | ArrayGroup (1 level only)" model.
// A tree of these mirrors the JSON payload's nesting EXACTLY, at any depth —
// objects containing objects, arrays containing arrays, arrays of objects that
// themselves contain arrays, etc.

interface SingleNode {
  kind: "single";
  key: string;
  displayName: string;
  label: string;
}

interface FolderNode {
  kind: "folder";
  label: string;
  fullPath: string;
  children: DisplayNode[];
}

interface ArrayNode {
  kind: "array";
  basePath: string;
  label: string;
  itemType: "primitive" | "object";
  indices: number[];
  // Per index, the recursively-built subtree living "inside" that index.
  // For itemType "object" this may itself contain nested folders/arrays.
  childrenByIndex: Record<number, DisplayNode[]>;
  // Every leaf param key reachable under that index, however deep.
  leafKeysByIndex: Record<number, string[]>;
}

type DisplayNode = SingleNode | FolderNode | ArrayNode;

type Token = { type: "key"; name: string } | { type: "index"; value: number };

const tokenizeKey = (key: string): Token[] => {
  const tokens: Token[] = [];
  let i = 0;
  let current = "";
  const flush = () => {
    if (current) {
      tokens.push({ type: "key", name: current });
      current = "";
    }
  };
  while (i < key.length) {
    const ch = key[i];
    if (ch === ".") {
      flush();
      i++;
    } else if (ch === "[") {
      flush();
      let j = i + 1;
      while (j < key.length && key[j] !== "]") j++;
      tokens.push({ type: "index", value: Number(key.slice(i + 1, j)) });
      i = j + 1;
    } else {
      current += ch;
      i++;
    }
  }
  flush();
  return tokens;
};

const getLeafName = (path: string): string => {
  const parts = path.split(".");
  return parts[parts.length - 1] ?? path;
};

interface TokenizedParam {
  tokens: Token[];
  item: ParamItem;
}

const collectLeafKeys = (nodes: DisplayNode[]): string[] => {
  const acc: string[] = [];
  const walk = (ns: DisplayNode[]) => {
    ns.forEach((n) => {
      if (n.kind === "single") acc.push(n.key);
      else if (n.kind === "folder") walk(n.children);
      else if (n.kind === "array") n.indices.forEach((idx) => walk(n.childrenByIndex[idx] ?? []));
    });
  };
  walk(nodes);
  return acc;
};

// Recursively groups tokenized params by their leading segment at each level.
// If a level's segments are ALL array-indices, that level IS an array — its
// indices become `indices`, and whatever remains under each index (fields,
// nested objects, or further nested arrays) is resolved by recursing.
const buildNodesFromTokens = (entries: TokenizedParam[], pathPrefix: string): DisplayNode[] => {
  const groups = new Map<string, { token: Token; items: TokenizedParam[] }>();
  const order: string[] = [];

  entries.forEach((entry) => {
    if (entry.tokens.length === 0) return;
    const head = entry.tokens[0];
    const groupKey = head.type === "index" ? `[${head.value}]` : `.${head.name}`;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, { token: head, items: [] });
      order.push(groupKey);
    }
    groups.get(groupKey)!.items.push({ tokens: entry.tokens.slice(1), item: entry.item });
  });

  const allIndex = order.length > 0 && order.every((k) => groups.get(k)!.token.type === "index");

  if (allIndex) {
    const indices = order
      .map((k) => (groups.get(k)!.token as { type: "index"; value: number }).value)
      .sort((a, b) => a - b);

    const childrenByIndex: Record<number, DisplayNode[]> = {};
    const leafKeysByIndex: Record<number, string[]> = {};
    let itemType: "primitive" | "object" = "primitive";

    indices.forEach((idx) => {
      const groupKey = `[${idx}]`;
      const group = groups.get(groupKey)!;
      const hasRemaining = group.items.some((e) => e.tokens.length > 0);
      if (hasRemaining) {
        itemType = "object";
        const childPath = `${pathPrefix}[${idx}]`;
        const childNodes = buildNodesFromTokens(group.items, childPath);
        childrenByIndex[idx] = childNodes;
        leafKeysByIndex[idx] = collectLeafKeys(childNodes);
      } else {
        // For primitive arrays, get the item for this specific index
        const leafItem = group.items[0]?.item;
        if (leafItem) {
          childrenByIndex[idx] = [{ kind: "single", key: leafItem.key, displayName: leafItem.displayName, label: `[${idx}]` }];
          leafKeysByIndex[idx] = [leafItem.key];
        }
      }
    });

    return [{
      kind: "array",
      basePath: pathPrefix,
      label: getLeafName(pathPrefix),
      itemType,
      indices,
      childrenByIndex,
      leafKeysByIndex,
    }];
  }

  return order.map((groupKey): DisplayNode => {
    const { token, items } = groups.get(groupKey)!;
    const name = (token as { type: "key"; name: string }).name;
    const childPath = pathPrefix ? `${pathPrefix}.${name}` : name;

    const leafEntry = items.find((e) => e.tokens.length === 0);
    if (leafEntry) {
      return { kind: "single", key: leafEntry.item.key, displayName: leafEntry.item.displayName, label: name };
    }

    const childNodes = buildNodesFromTokens(items, childPath);
    // Surface an array node directly (it already carries its own label) instead
    // of wrapping it in a redundant extra folder layer.
    if (childNodes.length === 1 && childNodes[0].kind === "array" && childNodes[0].basePath === childPath) {
      return childNodes[0];
    }
    return { kind: "folder", label: name, fullPath: childPath, children: childNodes };
  });
};

const buildDisplayTree = (params: ParamItem[]): DisplayNode[] => {
  const entries: TokenizedParam[] = params.map((item) => ({ tokens: tokenizeKey(item.key), item }));
  return buildNodesFromTokens(entries, "");
};

// Walks the whole tree (any depth) and returns every array node found — used to
// register nested arrays (e.g. "Data[0].SubItems") as their own independent
// array groups, not just the top-level ones.
const collectArrayNodes = (nodes: DisplayNode[]): ArrayNode[] => {
  const acc: ArrayNode[] = [];
  const walk = (ns: DisplayNode[]) => {
    ns.forEach((n) => {
      if (n.kind === "array") {
        acc.push(n);
        n.indices.forEach((idx) => walk(n.childrenByIndex[idx] ?? []));
      } else if (n.kind === "folder") {
        walk(n.children);
      }
    });
  };
  walk(nodes);
  return acc;
};

// Direct plain-field descendants of a node subtree, stopping at any nested
// array boundary (nested arrays are registered separately under their own
// basePath, so they must NOT be folded into this array's objectFields).
const collectDirectFieldPaths = (nodes: DisplayNode[]): string[] => {
  const acc: string[] = [];
  nodes.forEach((n) => {
    if (n.kind === "single") acc.push(n.key);
    else if (n.kind === "folder") acc.push(...collectDirectFieldPaths(n.children));
  });
  return acc;
};

const getObjectFieldsForNode = (node: ArrayNode): string[] => {
  if (node.itemType === "primitive" || node.indices.length === 0) return [];
  const refIdx = node.indices[0];
  const prefix = `${node.basePath}[${refIdx}]`;
  return collectDirectFieldPaths(node.childrenByIndex[refIdx] ?? []).map((k) => k.slice(prefix.length + 1));
};

const escapeRegExp = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const transformNodesToAllElements = (nodes: DisplayNode[], basePath: string): DisplayNode[] => {
  const indexPattern = new RegExp(`^${escapeRegExp(basePath)}\\[\\d+\\]`);
  const replacement = `${basePath}[*]`;

  return nodes.map((n): DisplayNode => {
    if (n.kind === "single") {
      const newKey = n.key.replace(indexPattern, replacement);
      return {
        ...n,
        key: newKey,
      };
    }
    if (n.kind === "folder") {
      const newPath = n.fullPath.replace(indexPattern, replacement);
      return {
        ...n,
        fullPath: newPath,
        children: transformNodesToAllElements(n.children, basePath),
      };
    }
    if (n.kind === "array") {
      const newBase = n.basePath.replace(indexPattern, replacement);
      const newChildren: Record<number, DisplayNode[]> = {};
      const newLeafs: Record<number, string[]> = {};
      n.indices.forEach((idx) => {
        const trans = transformNodesToAllElements(n.childrenByIndex[idx] ?? [], basePath);
        newChildren[idx] = trans;
        newLeafs[idx] = collectLeafKeys(trans);
      });
      return {
        ...n,
        basePath: newBase,
        childrenByIndex: newChildren,
        leafKeysByIndex: newLeafs,
      };
    }
    return n;
  });
};

const buildArrayStateSeed = (
  currentState: TmState | undefined,
  baseParams: ParamItem[],
  checkedOnly = false
): { extraKeys: string[] } => {
  const extraKeys: string[] = [];

  const existingKeys = new Set(baseParams.map((p) => p.key));
  const checkedParams = new Set(currentState?.checkedParams ?? []);

  // First, process registered array groups
  if (currentState?.arrayGroups) {
    Object.values(currentState.arrayGroups).forEach((group) => {
      group.indices.forEach((idx) => {
        const keysForIdx =
          group.itemType === "primitive"
            ? [`${group.basePath}[${idx}]`]
            : group.objectFields.map((field) => `${group.basePath}[${idx}].${field}`);

        const hasCheckedValidation = keysForIdx.some((key) => checkedParams.has(key));

        if (checkedOnly && !hasCheckedValidation) return;

        keysForIdx.forEach((key) => {
          if (!existingKeys.has(key) && !extraKeys.includes(key)) {
            extraKeys.push(key);
          }
        });
      });
    });
  }

  // Additionally, infer array indices from validations that aren't covered by base params or arrayGroups
  // This handles cases where validations exist for array indices beyond what's in the payload
  // and arrayGroups hasn't been registered yet
  if (currentState?.validations) {
    Object.keys(currentState.validations).forEach((key) => {
      if (!existingKeys.has(key) && !extraKeys.includes(key)) {
        // Check if this is an array index key (contains [idx])
        const arrayMatch = key.match(/^(.+)\[(\d+)\](.*)$/);
        if (arrayMatch) {
          const basePath = arrayMatch[1];
          const idx = Number(arrayMatch[2]);
          // If arrayGroup already exists for this basePath, do not resurrect indices not in the group
          if (currentState?.arrayGroups?.[basePath]) {
            if (!currentState.arrayGroups[basePath].indices.includes(idx)) {
              return;
            }
          }
          const hasCheckedValidation = checkedParams.has(key);
          if (!checkedOnly || hasCheckedValidation) {
            extraKeys.push(key);
          }
        }
      }
    });
  }

  return { extraKeys };
};

const getArrayGroupSignature = (arrayGroups: TmState["arrayGroups"]): string => {
  const groups = Object.values(arrayGroups ?? {}).map((group) => ({
    basePath: group.basePath,
    itemType: group.itemType,
    objectFields: [...group.objectFields].sort(),
    indices: [...group.indices].sort((a, b) => a - b),
  }));

  groups.sort((a, b) => a.basePath.localeCompare(b.basePath));
  return JSON.stringify(groups);
};

const getCheckedArrayParamSignature = (checkedParams: string[] | undefined): string => {
  return JSON.stringify((checkedParams ?? []).filter((key) => key.includes("[")).sort());
};

const TmParamPanel = ({ params, state, onToggle, onUpdateRule, onRegisterArrayGroup, onSetArrayMode, enumMappings, showInfo, tooltipPosition = "right", showScroll }: TmParamPanelProps): JSX.Element => {
  const [openTypeMenu, setOpenTypeMenu] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
    minWidth?: number;
    maxHeight?: number;
  }>({});
  const [optionTooltip, setOptionTooltip] = useState<{ primary: string; secondary?: string; x: number; y: number } | null>(null);
  const [customInputs, setCustomInputs] = useState<Record<string, string>>({});
  const [customOptions, setCustomOptions] = useState<Record<string, string[]>>({});
  const [dynamicArrayKeys, setDynamicArrayKeys] = useState<string[]>([]);
  // By default, array index rows are open / expanded; collapsed ones are stored here
  const [collapsedObjectIndices, setCollapsedObjectIndices] = useState<Set<string>>(new Set());
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!openTypeMenu) return;
    const handleClose = () => {
      setOpenTypeMenu(null);
      setOptionTooltip(null);
    };
    window.addEventListener("scroll", handleClose, true);
    window.addEventListener("resize", handleClose);
    return () => {
      window.removeEventListener("scroll", handleClose, true);
      window.removeEventListener("resize", handleClose);
    };
  }, [openTypeMenu]);

  const toggleMenu = (e: React.MouseEvent<HTMLButtonElement>, menuKey: string) => {
    if (openTypeMenu === menuKey) {
      setOpenTypeMenu(null);
    } else {
      const buttonRect = e.currentTarget.getBoundingClientRect();
      const isEnum = menuKey.endsWith("-enum");
      const estimatedWidth = isEnum ? 250 : Math.max(buttonRect.width, 100);
      const overflowRight = buttonRect.left + estimatedWidth > window.innerWidth - 12;

      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const shouldOpenUpwards = spaceBelow < 180 && spaceAbove > spaceBelow;
      const availableSpace = shouldOpenUpwards ? spaceAbove : spaceBelow;
      const computedMaxHeight = Math.max(100, Math.min(220, availableSpace - 12));

      setMenuPos({
        top: shouldOpenUpwards ? undefined : buttonRect.bottom + 4,
        bottom: shouldOpenUpwards ? window.innerHeight - buttonRect.top + 4 : undefined,
        left: overflowRight ? undefined : Math.max(8, buttonRect.left),
        right: overflowRight ? Math.max(8, window.innerWidth - buttonRect.right) : undefined,
        minWidth: buttonRect.width,
        maxHeight: computedMaxHeight,
      });
      setOpenTypeMenu(menuKey);
    }
  };

  const toggleFolderCollapsed = (fullPath: string): void => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(fullPath)) next.delete(fullPath);
      else next.add(fullPath);
      return next;
    });
  };

  const onRegisterArrayGroupRef = useRef(onRegisterArrayGroup);
  onRegisterArrayGroupRef.current = onRegisterArrayGroup;
  const registeredSignaturesRef = useRef<Record<string, string>>({});
  const paramKeySignature = useMemo(() => JSON.stringify(params.map((p) => p.key)), [params]);
  const arrayGroupSignature = useMemo(() => getArrayGroupSignature(state?.arrayGroups), [state?.arrayGroups]);
  const checkedArrayParamSignature = useMemo(() => getCheckedArrayParamSignature(state?.checkedParams), [state?.checkedParams]);

  useEffect(() => {
    const { extraKeys } = buildArrayStateSeed(state, params);
    setDynamicArrayKeys((prev) => {
      if (prev.length === extraKeys.length && prev.every((k, i) => k === extraKeys[i])) {
        return prev;
      }
      return extraKeys;
    });
  }, [arrayGroupSignature, paramKeySignature, checkedArrayParamSignature]);

  const allParams = useMemo(() => {
    const extra = dynamicArrayKeys.map((key) => ({ key, displayName: key }));
    const merged = [...params, ...extra];
    const seen = new Set<string>();
    return merged.filter((item) => {
      if (seen.has(item.key)) return false;
      seen.add(item.key);
      return true;
    });
  }, [params, dynamicArrayKeys]);

  const treeNodes = useMemo(() => buildDisplayTree(allParams), [allParams]);
  // Discovery tree from raw params only — avoids feeding dynamicArrayKeys' synthesized rows back into registration (was causing an infinite ping-pong).
  const discoveryTreeNodes = useMemo(() => buildDisplayTree(params), [params]);

  useEffect(() => {
    if (!enumMappings || !state) return;
    state.checkedParams.forEach((key) => {
      const type = state.validations[key]?.type;
      const keyEnumMap = enumMappings[key] ?? enumMappings[key.replace(/\[(\d+|\*)\]/g, "[0]")];
      if (keyEnumMap && type !== "constant" && type !== "enumMatch") {
        onUpdateRule(key, { type: "constant" });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(state?.checkedParams)]);

  useEffect(() => {
    const arrayNodes = collectArrayNodes(discoveryTreeNodes);
    arrayNodes.forEach((node) => {
      const objectFields = getObjectFieldsForNode(node);
      // Only grow the known index range here — shrinking stays exclusive to removeArrayIndex below.
      const existingIndices = state?.arrayGroups?.[node.basePath]?.indices ?? [];
      const indices = Array.from(new Set([...existingIndices, ...node.indices])).sort((a, b) => a - b);
      const signature = JSON.stringify({ itemType: node.itemType, objectFields, indices });
      if (registeredSignaturesRef.current[node.basePath] === signature) return;
      registeredSignaturesRef.current[node.basePath] = signature;
      onRegisterArrayGroupRef.current?.({
        basePath: node.basePath,
        itemType: node.itemType,
        objectFields,
        indices,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramKeySignature, arrayGroupSignature]);

  const isChecked = (key: string): boolean => state?.checkedParams.includes(key) ?? false;

  const toggleIndexExpanded = (expandKey: string): void => {
    setCollapsedObjectIndices((prev) => {
      const next = new Set(prev);
      if (next.has(expandKey)) next.delete(expandKey);
      else next.add(expandKey);
      return next;
    });
  };

  const toggleIndexChecked = (node: ArrayNode, idx: number): void => {
    const leafKeys = node.leafKeysByIndex[idx] ?? [];
    if (leafKeys.length === 0) return;
    const shouldCheck = !leafKeys.every((key) => isChecked(key));
    leafKeys.forEach((key) => {
      if (isChecked(key) !== shouldCheck) onToggle(key);
    });
    if (shouldCheck) {
      setCollapsedObjectIndices((prev) => {
        const next = new Set(prev);
        next.delete(`${node.basePath}[${idx}]`);
        return next;
      });
    }
  };

  const addArrayIndex = (node: ArrayNode): void => {
    const nextIndex = node.indices.length > 0 ? Math.max(...node.indices) + 1 : 0;

    let keysToAdd: string[];
    if (node.itemType === "primitive") {
      keysToAdd = [`${node.basePath}[${nextIndex}]`];
    } else if (node.indices.length > 0) {
      // Clone the shape of an existing sibling index — schema-driven arrays
      // share the same shape across indices, including any nested arrays
      // living inside each item.
      const templateIdx = node.indices[0];
      const templateKeys = node.leafKeysByIndex[templateIdx] ?? [];
      const oldPrefix = `${node.basePath}[${templateIdx}]`;
      const newPrefix = `${node.basePath}[${nextIndex}]`;
      keysToAdd = templateKeys.map((k) => newPrefix + k.slice(oldPrefix.length));
    } else {
      keysToAdd = [];
    }

    setDynamicArrayKeys((prev) => {
      const next = [...prev];
      keysToAdd.forEach((k) => {
        if (!next.includes(k)) next.push(k);
      });
      return next;
    });

    if (node.itemType === "object") {
      setCollapsedObjectIndices((prev) => {
        const next = new Set(prev);
        next.delete(`${node.basePath}[${nextIndex}]`);
        return next;
      });
    }

    onRegisterArrayGroup({
      basePath: node.basePath,
      itemType: node.itemType,
      objectFields: getObjectFieldsForNode(node),
      indices: [...node.indices, nextIndex],
    });
  };

  const removeArrayIndex = (node: ArrayNode): void => {
    if (node.indices.length === 0) return;

    const lastIndex = Math.max(...node.indices);
    const removePrefix = `${node.basePath}[${lastIndex}]`;

    const leafKeys = node.itemType === "primitive" ? [removePrefix] : (node.leafKeysByIndex[lastIndex] ?? []);
    leafKeys.forEach((key) => {
      if (isChecked(key)) onToggle(key);
    });

    // Prefix-match cascades correctly through any depth of nesting under this index.
    setDynamicArrayKeys((prev) => prev.filter((k) => k !== removePrefix && !k.startsWith(`${removePrefix}.`)));

    if (node.itemType === "object") {
      setCollapsedObjectIndices((prev) => {
        const next = new Set(prev);
        Array.from(next).forEach((k) => {
          if (k === removePrefix || k.startsWith(`${removePrefix}.`) || k.startsWith(`${removePrefix}[`)) {
            next.delete(k);
          }
        });
        return next;
      });
    }

    onRegisterArrayGroup({
      basePath: node.basePath,
      itemType: node.itemType,
      objectFields: getObjectFieldsForNode(node),
      indices: node.indices.filter((idx) => idx !== lastIndex),
    });
  };

  const renderTypeSelector = (key: string, rule: ValidationRule): JSX.Element => {
    const keyEnumMap = enumMappings?.[key] ?? enumMappings?.[key.replace(/\[(\d+|\*)\]/g, "[0]")];
    const isEnumParam = !!keyEnumMap;
    const validOptions = isEnumParam
      ? VALIDATION_OPTIONS.filter((o) => o.value === "constant" || o.value === "enumMatch")
      : VALIDATION_OPTIONS.filter((o) => o.value !== "enumMatch");

    return (
      <div className={`tcd-v-type-wrap${openTypeMenu === `${key}-type` ? ' is-open' : ''}`} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="tcd-v-type-btn"
          onClick={(e) => toggleMenu(e, `${key}-type`)}
        >
          <span className="tcd-v-type-btn__label">
            {VALIDATION_OPTIONS.find((o) => o.value === rule.type)?.label ?? (isEnumParam ? "Constant" : "Equal to")}
          </span>
          <ChevronDownSmIcon rotated={openTypeMenu === `${key}-type`} />
        </button>
        {openTypeMenu === `${key}-type` && createPortal(
          <>
            <div className="tcd-dropdown-backdrop" onClick={() => setOpenTypeMenu(null)} />
            <div
              className="tcd-v-type-menu"
              style={{
                position: 'fixed',
                top: menuPos.top,
                bottom: menuPos.bottom,
                left: menuPos.left,
                right: menuPos.right,
                minWidth: menuPos.minWidth,
                maxHeight: menuPos.maxHeight,
                zIndex: 9999,
              }}
            >
              {validOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`tcd-v-type-option${rule.type === opt.value ? " selected" : ""}`}
                  onClick={() => { onUpdateRule(key, { type: opt.value }); setOpenTypeMenu(null); }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>,
          document.body
        )}
      </div>
    );
  };

  const renderMinMaxInputs = (key: string, rule: ValidationRule): JSX.Element => (
    <div className="tcd-v-range" onClick={(e) => e.stopPropagation()}>
      <input
        className="tcd-v-input tcd-v-input--narrow"
        type="number"
        placeholder="Min"
        value={rule.min}
        onChange={(e) => onUpdateRule(key, { min: e.target.value })}
        onClick={(e) => e.stopPropagation()}
      />
      <span className="tcd-v-sep">-</span>
      <input
        className="tcd-v-input tcd-v-input--narrow"
        type="number"
        placeholder="Max"
        value={rule.max}
        onChange={(e) => onUpdateRule(key, { max: e.target.value })}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );

  const renderRuleInputs = (key: string, rule: ValidationRule): JSX.Element => {
    const keyEnumMap = enumMappings?.[key] ?? enumMappings?.[key.replace(/\[(\d+|\*)\]/g, "[0]")];
    const isEnumParam = !!keyEnumMap;

    if (isEnumParam && rule.type === "enumMatch") {
      return renderMinMaxInputs(key, rule);
    }

    if (isEnumParam) {
      return (
        <div className="tcd-v-enum-wrap" onClick={(e) => e.stopPropagation()}>
          <div className={`tcd-v-type-wrap tcd-v-type-wrap--flex${openTypeMenu === `${key}-enum` ? ' is-open' : ''}`}>
            <button
              type="button"
              className="tcd-v-type-btn tcd-v-enum-btn"
              onClick={(e) => toggleMenu(e, `${key}-enum`)}
            >
              <span className={`tcd-v-type-btn__label${rule.value ? "" : " tcd-v-enum-placeholder"}`}>
                {rule.value ? (keyEnumMap![rule.value] ?? rule.value) : "Select value"}
              </span>
              <ChevronDownSmIcon rotated={openTypeMenu === `${key}-enum`} />
            </button>
            {openTypeMenu === `${key}-enum` && createPortal(
              <>
                <div className="tcd-dropdown-backdrop" onClick={() => setOpenTypeMenu(null)} />
                <div
                  className="tcd-v-type-menu tcd-v-enum-menu"
                  style={{
                    position: 'fixed',
                    top: menuPos.top,
                    bottom: menuPos.bottom,
                    left: menuPos.left,
                    right: menuPos.right,
                    minWidth: menuPos.minWidth,
                    maxHeight: menuPos.maxHeight,
                    zIndex: 9999,
                  }}
                >
                  {Object.entries(keyEnumMap!).map(([enumKey, enumLabel]) => (
                    <button
                      key={enumKey}
                      type="button"
                      className={`tcd-v-type-option${rule.value === enumKey || rule.value === enumLabel ? " selected" : ""}`}
                      onClick={() => { onUpdateRule(key, { value: enumKey }); setOpenTypeMenu(null); setOptionTooltip(null); }}
                      onMouseEnter={(e) => {
                        const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                        setOptionTooltip({
                          primary: enumLabel,
                          secondary: enumKey !== enumLabel ? `value: ${enumKey}` : undefined,
                          x: tooltipPosition === "left" ? rect.left - 10 : rect.right + 10,
                          y: rect.top + rect.height / 2,
                        });
                      }}
                      onMouseLeave={() => setOptionTooltip(null)}
                    >
                      {enumLabel}
                    </button>
                  ))}
                  {(customOptions[key] ?? []).map((customVal) => (
                    <button
                      key={customVal}
                      type="button"
                      className={`tcd-v-type-option${rule.value === customVal ? " selected" : ""}`}
                      onClick={() => { onUpdateRule(key, { value: customVal }); setOpenTypeMenu(null); setOptionTooltip(null); }}
                      onMouseEnter={(e) => {
                        const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                        setOptionTooltip({ primary: customVal, x: tooltipPosition === "left" ? rect.left - 10 : rect.right + 10, y: rect.top + rect.height / 2 });
                      }}
                      onMouseLeave={() => setOptionTooltip(null)}
                    >
                      {customVal}
                    </button>
                  ))}
                  <div className="tcd-v-enum-custom-input-wrap" onClick={(e) => e.stopPropagation()}>
                    <input
                      className="tcd-v-enum-custom-input"
                      type="text"
                      placeholder="Enter custom value..."
                      value={customInputs[key] ?? ""}
                      onChange={(e) => setCustomInputs((prev) => ({ ...prev, [key]: e.target.value }))}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === "Enter") {
                          const val = (customInputs[key] ?? "").trim();
                          if (val) {
                            setCustomOptions((prev) => ({ ...prev, [key]: [...(prev[key] ?? []).filter((v) => v !== val), val] }));
                            onUpdateRule(key, { value: val });
                            setCustomInputs((prev) => ({ ...prev, [key]: "" }));
                            setOpenTypeMenu(null);
                          }
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              </>,
              document.body
            )}
          </div>
        </div>
      );
    }

    if (rule.type === "range") {
      return renderMinMaxInputs(key, rule);
    }

    return (
      <input
        className="tcd-v-input tcd-v-input--narrow"
        type="text"
        placeholder="Value"
        value={rule.value}
        onChange={(e) => onUpdateRule(key, { value: e.target.value })}
        onClick={(e) => e.stopPropagation()}
      />
    );
  };

  const renderNode = (node: DisplayNode, depth: number): JSX.Element[] => {
    const indent = depth * 16;
    const isNested = depth > 0;

    if (node.kind === "folder") {
      const isCollapsed = collapsedFolders.has(node.fullPath);
      return [
        <div key={`folder-${node.fullPath}`} className="tcd-tree-section">
          <div className={`tcd-tree-folder${isNested ? ' tcd-tree-node' : ''}${!isCollapsed ? ' expanded' : ''}`} style={{ paddingLeft: indent }}>
            <button
              type="button"
              className="tcd-folder-toggle-btn"
              onClick={() => toggleFolderCollapsed(node.fullPath)}
            >
              <ChevronDownSmIcon className={isCollapsed ? "collapsed" : ""} />
              <span className="tcd-tree-folder__label">{formatTreeHeaderLabel(node.label)}</span>
            </button>
          </div>
          {!isCollapsed && (
            <div
              className="tcd-tree-children-wrap"
              style={{ '--tree-indent': `${indent}px` } as React.CSSProperties}
            >
              {node.children.flatMap((child) => renderNode(child, depth + 1))}
            </div>
          )}
        </div>,
      ];
    }

    if (node.kind === "single") {
      const checked = isChecked(node.key);
      const rule = state?.validations[node.key];
      return [
        <div key={node.key} className={`tcd-param-item${isNested ? ' tcd-tree-node' : ''}`} style={{ paddingLeft: indent }}>
          <div className="tcd-param-row-content">
            <div className="tcd-param-left" onClick={() => onToggle(node.key)}>
              <span className={`tcd-param-cb${checked ? " checked" : ""}`}>{checked && <CheckMiniIcon />}</span>
              <span className="tcd-param-name" title={node.label}>{node.label}</span>
            </div>
            {checked && rule && (
              <div className="tcd-validation-row-inline">
                {renderTypeSelector(node.key, rule)}
                {renderRuleInputs(node.key, rule)}
              </div>
            )}
          </div>
        </div>,
      ];
    }

    // node.kind === "array"
    const isAllMode =
      state?.arrayModes?.[node.basePath] !== undefined
        ? state.arrayModes[node.basePath] === "all"
        : (state?.checkedParams ?? []).some(
            (k) => k === `${node.basePath}[*]` || k.startsWith(`${node.basePath}[*].`)
          );

    const handleModeChange = (mode: ArrayValidationMode) => {
      onSetArrayMode?.(node.basePath, mode);
    };

    const refIdx = node.indices[0] ?? 0;

    return [
      <div key={node.basePath} className={`tcd-param-item${isNested ? ' tcd-tree-node' : ''}`} style={{ paddingLeft: indent }}>
        <div className="tcd-param-row tcd-param-row--spread" style={{ gap: "12px" }}>
          <span className="tcd-param-name">
            {node.label}
            <span className="tcd-param-array-badge">[{node.indices.length}]</span>
          </span>
          <div className="tcd-array-actions-wrap" onClick={(e) => e.stopPropagation()}>
            <CustomToolTip
              title={
                <div className="tcd-array-tooltip-content">
                  <div className="tcd-array-tooltip-row">
                    <span className="tcd-array-tooltip-tag">ALL</span>
                    <span className="tcd-array-tooltip-desc">
                      Applies a uniform validation condition across every element in the array simultaneously.
                    </span>
                  </div>
                  <div className="tcd-array-tooltip-divider" />
                  <div className="tcd-array-tooltip-row">
                    <span className="tcd-array-tooltip-tag">BY INDEX</span>
                    <span className="tcd-array-tooltip-desc">
                      Configures individual validation conditions for specific element indices ([0], [1], etc.) independently.
                    </span>
                  </div>
                </div>
              }
              placement="top"
              maxWidth="260px"
              wordBreak="normal"
            >
              <button className="tcd-info-btn" type="button" tabIndex={-1} aria-label="Array validation mode info">
                <InfoIcon />
              </button>
            </CustomToolTip>
            <div className="tcd-array-mode-segmented">
              <button
                type="button"
                className={`tcd-array-mode-btn${isAllMode ? " active" : ""}`}
                onClick={() => handleModeChange("all")}
              >
                All
              </button>
              <button
                type="button"
                className={`tcd-array-mode-btn${!isAllMode ? " active" : ""}`}
                onClick={() => handleModeChange("indexed")}
              >
                By Index
              </button>
            </div>
            <div className={`tcd-array-index-btns${isAllMode ? " tcd-array-index-btns--hidden" : ""}`}>
              {node.indices.length > 0 && (
                <CustomToolTip title="Remove index" placement="top">
                  <button
                    type="button"
                    className="tcd-remove-array-btn tcd-array-btn--icon-only"
                    onClick={() => removeArrayIndex(node)}
                  >
                    <span className="tcd-remove-array-btn__minus">−</span>
                  </button>
                </CustomToolTip>
              )}
              <CustomToolTip title="Add index" placement="top">
                <button
                  type="button"
                  className="tcd-add-array-btn tcd-array-btn--icon-only"
                  onClick={() => addArrayIndex(node)}
                >
                  <span className="tcd-add-array-btn__plus">+</span>
                </button>
              </CustomToolTip>
            </div>
          </div>
        </div>

        {isAllMode && (
          <div
            className="tcd-tree-children-wrap"
            style={{ '--tree-indent': '0px' } as React.CSSProperties}
          >
            {node.itemType === "primitive" ? (
              (() => {
                const key = `${node.basePath}[*]`;
                const checked = isChecked(key);
                const rule = state?.validations[key] ?? createDefaultRule(`${node.basePath}[0]`, enumMappings);
                return (
                  <div key={key} className="tcd-array-element-row tcd-tree-node" style={{ paddingLeft: 16 }}>
                    <div className="tcd-param-row-content">
                      <div className="tcd-param-left" onClick={() => onToggle(key)}>
                        <span className={`tcd-param-cb${checked ? " checked" : ""}`}>{checked && <CheckMiniIcon />}</span>
                        <span className="tcd-all-element-wrap">
                          <span className="tcd-array-wildcard-badge">[*]</span>
                          <span className="tcd-all-element-label">All Elements</span>
                        </span>
                      </div>
                      {checked && (
                        <div className="tcd-validation-row-inline">
                          {renderTypeSelector(key, rule)}
                          {renderRuleInputs(key, rule)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()
            ) : (
              (() => {
                const transformed = transformNodesToAllElements(node.childrenByIndex[refIdx] ?? [], node.basePath);
                const leafKeys = collectLeafKeys(transformed);
                const checkedCount = leafKeys.filter((k) => isChecked(k)).length;
                const isAllChecked = leafKeys.length > 0 && checkedCount === leafKeys.length;
                const isPartialChecked = checkedCount > 0 && checkedCount < leafKeys.length;
                const expandKey = `${node.basePath}[*]`;
                const isExpanded = !collapsedObjectIndices.has(expandKey);

                const toggleAllElementLeaves = () => {
                  const shouldCheck = !isAllChecked;
                  leafKeys.forEach((k) => {
                    const cur = isChecked(k);
                    if (shouldCheck && !cur) onToggle(k);
                    else if (!shouldCheck && cur) onToggle(k);
                  });
                };

                return (
                  <div key={expandKey} className="tcd-array-validation tcd-tree-node" style={{ paddingLeft: 16 }}>
                    <div className="tcd-param-row-content">
                      <div className="tcd-param-left">
                        <span
                          className={`tcd-param-cb${isAllChecked ? " checked" : ""}${isPartialChecked ? " partial" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAllElementLeaves();
                          }}
                        >
                          {isAllChecked && <CheckMiniIcon />}
                          {isPartialChecked && <MinusMiniIcon />}
                        </span>
                        <button
                          type="button"
                          className="tcd-all-element-toggle"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleIndexExpanded(expandKey);
                          }}
                        >
                          <ChevronDownSmIcon rotated={!isExpanded} />
                          <span className="tcd-array-wildcard-badge">[*]</span>
                          <span className="tcd-all-element-label">All Elements</span>
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div
                        className="tcd-tree-children-wrap"
                        style={{ '--tree-indent': '16px' } as React.CSSProperties}
                      >
                        {transformed.flatMap((child) => renderNode(child, depth + 2))}
                      </div>
                    )}
                  </div>
                );
              })()
            )}
          </div>
        )}

        {!isAllMode && node.indices.length > 0 && (
          <div
            className="tcd-tree-children-wrap"
            style={{ '--tree-indent': '0px' } as React.CSSProperties}
          >
            {node.itemType === "primitive" && node.indices.map((idx) => {
              const leafNode = node.childrenByIndex[idx]?.[0] as SingleNode | undefined;
              if (!leafNode) return null;
              const key = leafNode.key;
              const checked = isChecked(key);
              const rule = state?.validations[key];
              return (
                <div key={key} className="tcd-array-element-row tcd-tree-node" style={{ paddingLeft: 16 }}>
                  <div className="tcd-param-row-content">
                    <div className="tcd-param-left" onClick={() => onToggle(key)}>
                      <span className={`tcd-param-cb${checked ? " checked" : ""}`}>{checked && <CheckMiniIcon />}</span>
                      <span className="tcd-array-element-index">[{idx}]</span>
                    </div>
                    {checked && rule && (
                      <div className="tcd-validation-row-inline">
                        {renderTypeSelector(key, rule)}
                        {renderRuleInputs(key, rule)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {node.itemType === "object" && node.indices.map((idx) => {
              const expandKey = `${node.basePath}[${idx}]`;
              const isExpanded = !collapsedObjectIndices.has(expandKey);
              const leafKeys = node.leafKeysByIndex[idx] ?? [];
              const checkedCount = leafKeys.filter((k) => isChecked(k)).length;
              const isIndexChecked = leafKeys.length > 0 && checkedCount === leafKeys.length;
              const isIndexPartial = checkedCount > 0 && checkedCount < leafKeys.length;

              return (
                <div key={expandKey} className="tcd-array-validation tcd-tree-node" style={{ paddingLeft: 16 }}>
                  <div className="tcd-param-row-content">
                    <div className="tcd-param-left">
                      <span
                        className={`tcd-param-cb${isIndexChecked ? " checked" : ""}${isIndexPartial ? " partial" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleIndexChecked(node, idx);
                        }}
                      >
                        {isIndexChecked && <CheckMiniIcon />}
                        {isIndexPartial && <MinusMiniIcon />}
                      </span>
                      <span className="tcd-array-element-index">
                        <button
                          type="button"
                          className="tcd-array-obj-toggle"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleIndexExpanded(expandKey);
                          }}
                        >
                          <ChevronDownSmIcon rotated={!isExpanded} />
                          [{idx}]
                        </button>
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div
                      className="tcd-tree-children-wrap"
                      style={{ '--tree-indent': '16px' } as React.CSSProperties}
                    >
                      {(node.childrenByIndex[idx] ?? []).flatMap((child) => renderNode(child, depth + 2))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>,
    ];
  };

  return (
    <>
      <div className={`tcd-param-panel${showScroll ? " h-100" : ""}`}>
        {showInfo !== false && (
          <div className="tcd-param-hint">
            <InfoIcon />
            <span>Select parameters to configure validation</span>
          </div>
        )}
        <div className={`tcd-param-list${showScroll ? " tcd-param-list--scrollable" : ""}`}>
          {treeNodes.flatMap((node) => renderNode(node, 0))}
        </div>
      </div>
      {optionTooltip && createPortal(
        <div
          className={`tcd-option-tooltip${tooltipPosition === "left" ? " tcd-option-tooltip--left" : ""}`}
          style={{ left: optionTooltip.x, top: optionTooltip.y, transform: tooltipPosition === "left" ? "translateX(-100%) translateY(-50%)" : "translateY(-50%)" }}
        >
          <span className="tcd-option-tooltip__primary">{optionTooltip.primary}</span>
          {optionTooltip.secondary && (
            <span className="tcd-option-tooltip__secondary">{optionTooltip.secondary}</span>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default TmParamPanel;